import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { fetchYouTubeEditsVideos } from '../services/youtube';
import type { YouTubeVideo } from '../services/youtube';

export default function YouTubeEdits() {
  const [playingVideo, setPlayingVideo] = useState<string | null>(null);
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadVideos = async () => {
      try {
        const youtubeEdits = await fetchYouTubeEditsVideos();
        setVideos(youtubeEdits);
      } catch (error) {
        console.error('Error loading YouTube edits:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadVideos();
  }, []);

  const handleVideoClick = (videoId: string) => {
    if (playingVideo === videoId) {
      setPlayingVideo(null);
    } else {
      setPlayingVideo(videoId);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Unified Autoplay Hero Video with Header Text */}
      <section className="relative w-full overflow-hidden flex items-center justify-center pt-32 pb-24" style={{ minHeight: '60vh' }}>
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full"
            style={{ objectFit: 'cover', objectPosition: 'center center' }}
          >
            <source src="https://res.cloudinary.com/ddsnj941k/video/upload/video3_umtwtw.mp4" type="video/mp4" />
          </video>
          {/* Dark Overlays */}
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-[#0a0a0a]" />
          {/* Glow Edge */}
          <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 120px rgba(11, 227, 197, 0.12)' }} />
        </div>

        <div className="container-custom relative z-10 w-full mt-10">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[#0be3c5] text-sm tracking-[0.3em] uppercase mb-4 block" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
              Portfolio
            </span>
            <h1 className="text-5xl md:text-7xl font-bold mt-2 mb-6 text-white" style={{ textShadow: '0 4px 40px rgba(0,0,0,0.8)' }}>
              YouTube Editing
            </h1>
            <p className="text-white/80 text-lg md:text-xl font-medium" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}>
              Professional YouTube video editing with cinematic storytelling, motion graphics, and VFX.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Videos Grid Section */}
      <section className="section-padding bg-[#0a0a0a]">
        <div className="container-custom">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-white/40">Loading...</div>
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-white/40">No videos found. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {videos.map((video, index) => (
                <motion.div
                  key={video.id}
                  className="group relative rounded-lg overflow-hidden bg-white/5"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="relative overflow-hidden" style={{ boxShadow: '0 0 40px rgba(6, 182, 212, 0.15)' }}>
                    {playingVideo === video.videoId ? (
                      <div className="relative aspect-video">
                        <iframe
                          src={`https://www.youtube.com/embed/${video.videoId}?autoplay=1&enablejsapi=1&origin=${window.location.origin}`}
                          title={video.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          referrerPolicy="strict-origin-when-cross-origin"
                        />
                        <button
                          onClick={() => setPlayingVideo(null)}
                          className="absolute top-2 right-2 p-2 bg-black/60 hover:bg-black/80 rounded-lg backdrop-blur-sm transition-colors z-10"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-white">
                            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <div className="relative aspect-video cursor-pointer" onClick={() => handleVideoClick(video.videoId)}>
                        <img
                          src={`https://img.youtube.com/vi/${video.videoId}/maxresdefault.jpg`}
                          alt={video.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-colors" />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center glow-cyan">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="white" className="ml-1">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4">
                    <h3 className="text-white font-medium line-clamp-2 md:text-sm lg:text-base">{video.title}</h3>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
